<template>
  <div :id="mapId" :style="styles" />
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  model: {
    prop: 'data',
    event: 'set-object'
  },

  props: {
    show: { type: Boolean, default: () => false },

    data: {
      type: Object,
      default: () => {}
    },

    mapId: {
      type: String,
      default: () => 'map'
    },

    dark: {
      type: Boolean,
      default: () => false
    },

    width: {
      type: String,
      default: () => '100%'
    },

    height: {
      type: String,
      default: () => '100%'
    },

    mode: {
      type: String,
      default: () => 'supercourse'
    },
    error: {
      type: String,
      default: () => ''
    }
  },

  data () {
    return {
      map: undefined,
      google: null,
      objectMarker: null,
      objectPolygon: [],
      drawer: undefined,
      drawCourse: null,
      drawHoles: {},
      position: null,
      listeners: {}
    }
  },

  computed: {
    ...mapGetters(['color']),

    object: {
      get () {
        return this.data
      },

      set (value) {
        this.$emit('set-object', value)
      }
    },

    styles () {
      return `
        width: ${this.width};
        height: ${this.height};
        z-index: 1;
      `
    },

    polygonColor () {
      if (this.position === null) {
        return this.object.color
      }
      return this.object.holeColor
    },

    errorMessage () {
      if (this.error.length > 0) {
        return this.error
      }
      return null
    }
  },

  watch: {
    show (value) {
      if (value) {
        if (this.map) {
          if (this.object.id) {
            this.initializeHoles()
          } else {
            this.setInitialLocation()
          }
        }
      } else {
        if (this.objectPolygon) {
          this.removePolygon()
        }
      }
    }
  },

  beforeCreate () {
    /* eslint-disable no-undef, no-unused-vars */
    this.$googlemaps.load().then(() => {
      this.google = google
      this.initMap()
    })
    /* eslint-enable */
  },

  created () {},

  mounted () {},

  beforeDestroy () {},

  methods: {
    async initMap () {
      const initialLocation = { lat: 0, lng: 0 }
      this.map = new this.google.maps.Map(
        document.getElementById(this.mapId),
        {
          zoom: 15,
          // mapTypeControl: true,
          streetViewControl: false,
          center: initialLocation,
          scaleControl: true
        }
      )

      this.setInitialLocation()

      setTimeout(() => {
        if (!this.object.id) {
          // return
        } else {
          this.initializeHoles()
        }
        this.initDrawer()
      }, 0)
    },

    setInitialLocation () {
      let initialLocation = { lat: 0, lng: 0 }

      const self = this
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(function (position) {
          initialLocation = new self.google.maps.LatLng(
            position.coords.latitude, position.coords.longitude)
          if (!!self.object && !self.object.id) {
            self.map.setCenter(initialLocation)
          }
          console.log('initMap ~ initialLocation', initialLocation)
        })
      }
    },

    /**
     * Initialize Google Map Drawer
     */
    initDrawer () {
      const google = this.google
      this.drawer = new google.maps.drawing.DrawingManager({
        drawingMode: google.maps.drawing.OverlayType.POLYGON,
        drawingControl: false,
        drawingControlOptions: {
          position: google.maps.ControlPosition.RIGHT,
          drawingModes: [
            google.maps.drawing.OverlayType.CIRCLE,
            google.maps.drawing.OverlayType.POLYGON,
            google.maps.drawing.OverlayType.POLYLINE
          ]
        },
        polygonOptions: {
          fillColor: this.polygonColor, // color
          strokeColor: this.polygonColor, // color
          fillOpacity: 0.35,
          strokeOpacity: 0.8,
          strokeWeight: 2,
          clickable: false,
          editable: false,
          zIndex: 1
        }
      })

      google.maps.event.addListener(this.drawer, 'overlaycomplete', this.attach)
    },

    attach (event) {
      /**
       * Erase the previous overlay if it exist
       */
      if (this.position === null) {
        if (this.drawCourse) {
          this.drawCourse.setMap(null)
        }
      } else {
        this.resetHole()
      }

      const key = this.position === null ? 'supercourse' : this.position - 1
      if (this.objectPolygon.length === 1 && this.position === null) {
        // console.log('aca')
        this.google.maps.event.removeListener(this.listeners[key])
      } else {
        this.objectPolygon.forEach((hole, position) => {
          this.google.maps.event.removeListener(this.listeners[position - 1])
        })
      }

      this.stopDrawing()
      const { overlay } = event
      const path = []

      const vertices = overlay.getPath()

      for (let i = 0; i < vertices.getLength(); i++) {
        const xy = vertices.getAt(i)
        path.push({
          latitude: xy.lat(),
          longitude: xy.lng()
        })
      }

      if (this.object.parentId === null && this.position === null) {
        this.object.points = path
        this.drawCourse = overlay
        this.listeners[key] = this.google.maps.event.addListener(this.drawCourse.getPath(), 'set_at', this.updateDrawCourse)
      } else {
        this.object.holes[this.position - 1].points = path
        this.drawHoles[this.position - 1] = overlay
        this.listeners[key] = this.google.maps.event.addListener(this.drawHoles[key].getPath(), 'set_at', this.updateDrawHoles)
      }
    },

    initializeHoles () {
      if (this.object.parentId) {
        for (const hole of this.object.holes) {
          this.paintPolygon(hole.points, this.object.holeColor)
        }
        this.centerInMap(this.object.holes[0].points)
      } else {
        if (this.object.points.length !== 0) {
          this.paintPolygon(this.object.points, this.object.color)
        }
        for (const hole of this.object.holes) {
          if (hole.id) {
            this.paintPolygon(hole.points, this.object.holeColor)
          }
        }
        if (this.object.points.length !== 0) {
          this.centerInMap(this.object.points)
        } else {
          this.centerInMap(this.object.holes[0].points)
        }
      }
    },

    setPolygon () {
      if (this.map && this.objectPolygon) {
        if (this.objectPolygon.length === 1) {
          // console.log('aca')
          const path = this.objectPolygon[0].getPath()

          this.drawCourse = new this.google.maps.Polygon({
            paths: path,
            strokeColor: this.getPolygonColor('supercourse'),
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: this.getPolygonColor('supercourse'),
            fillOpacity: 0.35
          })
          this.drawCourse.setMap(this.map)
          this.drawCourse.setEditable(true)
          this.objectPolygon[0].setMap(null)
          this.objectPolygon = []

          const key = this.position === null ? 'supercourse' : this.position - 1

          this.listeners[key] = this.google.maps.event.addListener(this.drawCourse.getPath(), 'set_at', this.updateDrawCourse)
        } else {
          this.objectPolygon.forEach((hole, position) => {
            const path = hole.getPath()

            this.drawHoles[position] = new this.google.maps.Polygon({
              paths: path,
              strokeColor: this.getPolygonColor('hole'),
              strokeOpacity: 0.8,
              strokeWeight: 2,
              fillColor: this.getPolygonColor('hole'),
              fillOpacity: 0.35
            })

            this.drawHoles[position].setMap(this.map)
            this.drawHoles[position].setEditable(true)
            hole.setMap(null)
            this.listeners[position] = this.google.maps.event.addListener(this.drawHoles[position].getPath(), 'set_at', this.updateDrawHoles)
          })
          this.objectPolygon = []
        }
      }
    },

    updateDrawCourse (event) {
      console.log('draw course')
      const path = []

      const vertices = this.drawCourse.getPath()

      for (let i = 0; i < vertices.getLength(); i++) {
        const xy = vertices.getAt(i)
        path.push({
          latitude: xy.lat(),
          longitude: xy.lng()
        })
      }

      this.object.points = path
    },

    updateDrawHoles (event) {
      console.log('draw hole')
      this.object.holes.forEach((_, i) => {
        if (this.drawHoles[i]) {
          const path = []
          const vertices = this.drawHoles[i].getPath()

          for (let i = 0; i < vertices.getLength(); i++) {
            const xy = vertices.getAt(i)
            path.push({
              latitude: xy.lat(),
              longitude: xy.lng()
            })
          }

          this.object.holes[i].points = path
        }
      })
    },

    paintPolygon (points, color) {
      const coors = points.map(el => {
        return {
          lat: el.latitude,
          lng: el.longitude
        }
      })

      this.objectPolygon.push(new this.google.maps.Polygon({
        paths: coors,
        strokeColor: color,
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: color,
        fillOpacity: 0.35
      }))

      this.objectPolygon[this.objectPolygon.length - 1].setMap(this.map)
    },

    centerInMap (points, useBounds) {
      const coors = points.map(el => {
        return new this.google.maps.LatLng(el.latitude, el.longitude)
      })
      var bounds = new this.google.maps.LatLngBounds()
      var i

      for (i = 0; i < coors.length; i++) {
        bounds.extend(coors[i])
      }
      if (useBounds) {
        this.map.fitBounds(bounds)
      } else {
        this.map.setCenter(bounds.getCenter())
      }
      // this.map.fitBounds(bounds)
      // this.map.setCenter(bounds.getCenter())
    },

    zoneColor (type) {
      let result = '#0070A8'
      switch (type) {
        case 'RESTRICTED':
          result = '#FFC107'
          break

        case 'DANGEROUS':
          result = '#F44336'
          break

        default:
          break
      }

      return result
    },

    removePolygon () {
      for (const obj of this.objectPolygon) {
        obj.setMap(null)
      }
      this.objectPolygon = []
    },

    removeMarker () {
      this.objectMarker.setMap(null)
      this.objectMarker = null
    },

    placeMarker (location) {
      this.objectMarker = new this.google.maps.Marker({
        position: location,
        map: this.map
      })
      setTimeout(() => {
        this.map.setCenter(this.objectMarker.getPosition())
      }, 100)
    },

    drawPolygon (position) {
      this.position = position
      this.drawer.setOptions({
        drawingMode: this.google.maps.drawing.OverlayType.POLYGON,
        polygonOptions: {
          fillColor: this.polygonColor, // color
          strokeColor: this.polygonColor, // color
          fillOpacity: 0.3,
          strokeOpacity: 1,
          strokeWeight: 4,
          clickable: false,
          editable: true,
          zIndex: 1
        }
      })

      this.drawer.setMap(this.map)
    },

    setColor () {
      if (this.drawCourse) {
        this.drawCourse.setOptions({
          fillColor: this.getPolygonColor('supercourse'),
          strokeColor: this.getPolygonColor('supercourse')
        })
      }
      Object.keys(this.drawHoles).forEach(el => {
        if (this.drawHoles[el]) {
          this.drawHoles[el].setOptions({
            fillColor: this.getPolygonColor('hole'),
            strokeColor: this.getPolygonColor('hole')
          })
        }
      })
      // if (this.object.parentId === null && this.position === null) {
      // }
    },

    stopDrawing () {
      this.drawer.setMap(null)
    },

    cleanPolygons () {
      if (this.drawCourse) {
        this.drawCourse.setMap(null)
      }
      this.drawCourse = null
      this.resetHoles()
    },

    resetHole () {
      if (this.drawHoles[this.position - 1]) {
        this.drawHoles[this.position - 1].setMap(null)
        this.drawHoles[this.position - 1] = null
      }
    },

    resetHoles () {
      Object.keys(this.drawHoles).forEach(el => {
        if (this.drawHoles[el]) {
          this.drawHoles[el].setMap(null)
          this.drawHoles[el] = null
        }
      })
    },

    getPolygonColor (mode) {
      if (mode === 'supercourse') {
        return this.object.color
      }
      return this.object.holeColor
    }
  }
}
</script>
